import { apiRequest } from './client';
import type { MemoryCreateResult, MemoryListItem, MemoryRead } from './types';

function extensionForMime(mimeType: string): string {
  if (mimeType.includes('webm')) return 'webm';
  if (mimeType.includes('mp4')) return 'mp4';
  if (mimeType.includes('mpeg') || mimeType.includes('mp3')) return 'mp3';
  if (mimeType.includes('wav')) return 'wav';
  return 'webm';
}

function baseMimeType(mimeType: string): string {
  return mimeType.split(';')[0]?.trim().toLowerCase() || 'audio/webm';
}

export function uploadMemoryAudio(params: {
  userId: string;
  audioBlob: Blob;
  eventDate?: string;
  durationSeconds?: number;
  photos?: File[];
  processSync?: boolean;
}): Promise<MemoryCreateResult> {
  const mimeType = baseMimeType(params.audioBlob.type || 'audio/webm');
  const extension = extensionForMime(mimeType);
  const audioBlob = params.audioBlob.type === mimeType
    ? params.audioBlob
    : new Blob([params.audioBlob], { type: mimeType });
  const formData = new FormData();

  formData.append('user_id', params.userId);
  if (params.eventDate) {
    formData.append('event_date', params.eventDate);
  }
  formData.append('audio', audioBlob, `memory-${Date.now()}.${extension}`);
  params.photos?.forEach((photo) => {
    formData.append('photos', photo, photo.name);
  });

  return apiRequest<MemoryCreateResult>(
    `/memories/audio?process_sync=${params.processSync ?? true}`,
    {
      method: 'POST',
      body: formData,
    },
  );
}

export function listMemories(params: {
  userId: string;
  emotion?: string | null;
  search?: string | null;
  includeDeleted?: boolean;
  deletedOnly?: boolean;
  limit?: number;
  offset?: number;
}): Promise<MemoryListItem[]> {
  const query = new URLSearchParams({
    user_id: params.userId,
    limit: String(params.limit ?? 100),
    offset: String(params.offset ?? 0),
  });
  if (params.emotion) query.set('emotion', params.emotion);
  if (params.search) query.set('search', params.search);
  if (params.includeDeleted) query.set('include_deleted', 'true');
  if (params.deletedOnly) query.set('deleted_only', 'true');
  return apiRequest<MemoryListItem[]>(`/memories?${query.toString()}`);
}

export function updateMemory(memoryId: string, payload: {
  title?: string;
  story_text?: string;
  event_date?: string;
}): Promise<MemoryRead> {
  return apiRequest<MemoryRead>(`/memories/${memoryId}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

export function getMemory(memoryId: string): Promise<MemoryRead> {
  return apiRequest<MemoryRead>(`/memories/${memoryId}`);
}

export function uploadMemoryPhotos(memoryId: string, photos: File[]): Promise<MemoryRead> {
  const formData = new FormData();
  photos.forEach((photo) => {
    formData.append('photos', photo, photo.name);
  });

  return apiRequest<MemoryRead>(`/memories/${memoryId}/photos`, {
    method: 'POST',
    body: formData,
  });
}

export function deleteMemoryPhoto(memoryId: string, assetId: string): Promise<MemoryRead> {
  return apiRequest<MemoryRead>(`/memories/${memoryId}/photos/${assetId}`, {
    method: 'DELETE',
  });
}

export function deleteMemory(memoryId: string): Promise<MemoryRead> {
  return apiRequest<MemoryRead>(`/memories/${memoryId}`, {
    method: 'DELETE',
  });
}

export function restoreMemory(memoryId: string): Promise<MemoryRead> {
  return apiRequest<MemoryRead>(`/memories/${memoryId}/restore`, {
    method: 'PATCH',
  });
}

export function permanentlyDeleteMemory(memoryId: string): Promise<MemoryRead> {
  return apiRequest<MemoryRead>(`/memories/${memoryId}/permanent`, {
    method: 'DELETE',
  });
}
